/**
 * Screenshot Annotator Component
 *
 * Canvas-based annotation tool for screenshots.
 * Supports rectangles, arrows, text, and blur/redact areas.
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import type { Screenshot, Annotation } from '../hooks/useScreenshotCapture';
import clsx from 'clsx';

export type AnnotationTool = 'rectangle' | 'arrow' | 'text' | 'blur' | 'select';

interface ScreenshotAnnotatorProps {
  screenshot: Screenshot;
  onAnnotationAdd: (annotation: Omit<Annotation, 'id'>) => void;
  onAnnotationRemove: (annotationId: string) => void;
  onSave: (flattenedDataUrl: string) => void;
  onCancel: () => void;
  className?: string;
}

// Color palette for annotations
const COLORS = ['#FFEB3B', '#FF5722', '#2196F3', '#4CAF50', '#9C27B0', '#F44336'];

// Icons
const RectangleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const TextIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="4 7 4 4 20 4 20 7" />
    <line x1="9" y1="20" x2="15" y2="20" />
    <line x1="12" y1="4" x2="12" y2="20" />
  </svg>
);

const BlurIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="3" y1="15" x2="21" y2="15" />
    <line x1="9" y1="3" x2="9" y2="21" />
    <line x1="15" y1="3" x2="15" y2="21" />
  </svg>
);

const UndoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 10h10a5 5 0 0 1 0 10H9" />
    <polyline points="7 6 3 10 7 14" />
  </svg>
);

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export function ScreenshotAnnotator({
  screenshot,
  onAnnotationAdd,
  onAnnotationRemove,
  onSave,
  onCancel,
  className,
}: ScreenshotAnnotatorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [tool, setTool] = useState<AnnotationTool>('rectangle');
  const [color, setColor] = useState(COLORS[0]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState<{ x: number; y: number } | null>(null);
  const [textInput, setTextInput] = useState('');
  const [textPosition, setTextPosition] = useState<{ x: number; y: number } | null>(null);
  const [scale, setScale] = useState(1);

  const imageRef = useRef<HTMLImageElement | null>(null);

  // Load image and calculate scale
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imageRef.current = img;

      // Calculate scale to fit in container
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth - 40; // padding
        const containerHeight = containerRef.current.clientHeight - 150; // toolbar + actions
        const scaleX = containerWidth / img.width;
        const scaleY = containerHeight / img.height;
        const newScale = Math.min(scaleX, scaleY, 1); // Don't scale up
        setScale(newScale);
      }

      renderCanvas();
    };
    img.src = screenshot.dataUrl;
  }, [screenshot.dataUrl]);

  // Re-render canvas when annotations change
  useEffect(() => {
    renderCanvas();
  }, [screenshot.annotations, scale]);

  /**
   * Render the canvas with image and annotations
   */
  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const img = imageRef.current;

    if (!canvas || !ctx || !img) return;

    // Set canvas size
    canvas.width = img.width * scale;
    canvas.height = img.height * scale;

    // Clear and draw image
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // Draw annotations
    (screenshot.annotations || []).forEach((annotation) => {
      drawAnnotation(ctx, annotation, scale);
    });
  }, [screenshot.annotations, scale]);

  /**
   * Draw a single annotation on canvas
   */
  const drawAnnotation = (ctx: CanvasRenderingContext2D, annotation: Annotation, s: number) => {
    const annotationColor = annotation.color || COLORS[0];

    switch (annotation.type) {
      case 'rectangle':
        ctx.strokeStyle = annotationColor;
        ctx.lineWidth = 3;
        ctx.strokeRect(
          annotation.x * s,
          annotation.y * s,
          (annotation.width || 0) * s,
          (annotation.height || 0) * s
        );
        break;

      case 'arrow':
        drawArrow(
          ctx,
          annotation.x * s,
          annotation.y * s,
          (annotation.endX || annotation.x) * s,
          (annotation.endY || annotation.y) * s,
          annotationColor
        );
        break;

      case 'text':
        ctx.fillStyle = annotationColor;
        ctx.font = `bold ${16 * s}px Arial`;
        ctx.fillText(annotation.text || '', annotation.x * s, annotation.y * s);
        break;

      case 'blur':
        // Apply pixelated blur effect
        applyBlur(
          ctx,
          annotation.x * s,
          annotation.y * s,
          (annotation.width || 0) * s,
          (annotation.height || 0) * s
        );
        break;
    }
  };

  /**
   * Draw arrow annotation
   */
  const drawArrow = (
    ctx: CanvasRenderingContext2D,
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
    arrowColor: string
  ) => {
    const headlen = 15;
    const angle = Math.atan2(toY - fromY, toX - fromX);

    ctx.strokeStyle = arrowColor;
    ctx.fillStyle = arrowColor;
    ctx.lineWidth = 3;

    // Line
    ctx.beginPath();
    ctx.moveTo(fromX, fromY);
    ctx.lineTo(toX, toY);
    ctx.stroke();

    // Arrow head
    ctx.beginPath();
    ctx.moveTo(toX, toY);
    ctx.lineTo(toX - headlen * Math.cos(angle - Math.PI / 6), toY - headlen * Math.sin(angle - Math.PI / 6));
    ctx.lineTo(toX - headlen * Math.cos(angle + Math.PI / 6), toY - headlen * Math.sin(angle + Math.PI / 6));
    ctx.closePath();
    ctx.fill();
  };

  /**
   * Apply pixelated blur effect to area
   */
  const applyBlur = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number
  ) => {
    if (width <= 0 || height <= 0) return;

    const pixelSize = 10;
    const imageData = ctx.getImageData(x, y, width, height);
    const data = imageData.data;

    for (let py = 0; py < height; py += pixelSize) {
      for (let px = 0; px < width; px += pixelSize) {
        // Average color in block
        let r = 0, g = 0, b = 0, count = 0;

        for (let dy = 0; dy < pixelSize && py + dy < height; dy++) {
          for (let dx = 0; dx < pixelSize && px + dx < width; dx++) {
            const i = ((py + dy) * width + (px + dx)) * 4;
            r += data[i];
            g += data[i + 1];
            b += data[i + 2];
            count++;
          }
        }

        r = Math.floor(r / count);
        g = Math.floor(g / count);
        b = Math.floor(b / count);

        // Fill block with average color
        for (let dy = 0; dy < pixelSize && py + dy < height; dy++) {
          for (let dx = 0; dx < pixelSize && px + dx < width; dx++) {
            const i = ((py + dy) * width + (px + dx)) * 4;
            data[i] = r;
            data[i + 1] = g;
            data[i + 2] = b;
          }
        }
      }
    }

    ctx.putImageData(imageData, x, y);
  };

  /**
   * Get mouse position relative to canvas
   */
  const getCanvasPosition = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / scale,
      y: (e.clientY - rect.top) / scale,
    };
  };

  /**
   * Handle mouse down on canvas
   */
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getCanvasPosition(e);

    if (tool === 'text') {
      setTextPosition(pos);
      return;
    }

    setIsDrawing(true);
    setStartPos(pos);
  };

  /**
   * Handle mouse move on canvas
   */
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !startPos) return;

    const pos = getCanvasPosition(e);

    // Preview annotation while drawing
    renderCanvas();

    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    switch (tool) {
      case 'rectangle':
      case 'blur':
        ctx.strokeStyle = tool === 'blur' ? '#666' : color;
        ctx.lineWidth = 3;
        ctx.setLineDash(tool === 'blur' ? [5, 5] : []);
        ctx.strokeRect(
          startPos.x * scale,
          startPos.y * scale,
          (pos.x - startPos.x) * scale,
          (pos.y - startPos.y) * scale
        );
        ctx.setLineDash([]);
        break;

      case 'arrow':
        drawArrow(ctx, startPos.x * scale, startPos.y * scale, pos.x * scale, pos.y * scale, color);
        break;
    }
  };

  /**
   * Handle mouse up on canvas
   */
  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !startPos) {
      setIsDrawing(false);
      return;
    }

    const pos = getCanvasPosition(e);
    setIsDrawing(false);

    // Calculate dimensions
    const width = pos.x - startPos.x;
    const height = pos.y - startPos.y;

    // Ignore tiny annotations
    if (Math.abs(width) < 5 && Math.abs(height) < 5 && tool !== 'arrow') {
      setStartPos(null);
      return;
    }

    // Create annotation
    switch (tool) {
      case 'rectangle':
        onAnnotationAdd({
          type: 'rectangle',
          x: Math.min(startPos.x, pos.x),
          y: Math.min(startPos.y, pos.y),
          width: Math.abs(width),
          height: Math.abs(height),
          color,
        });
        break;

      case 'arrow':
        onAnnotationAdd({
          type: 'arrow',
          x: startPos.x,
          y: startPos.y,
          endX: pos.x,
          endY: pos.y,
          color,
        });
        break;

      case 'blur':
        onAnnotationAdd({
          type: 'blur',
          x: Math.min(startPos.x, pos.x),
          y: Math.min(startPos.y, pos.y),
          width: Math.abs(width),
          height: Math.abs(height),
        });
        break;
    }

    setStartPos(null);
  };

  /**
   * Handle text annotation submit
   */
  const handleTextSubmit = () => {
    if (textPosition && textInput.trim()) {
      onAnnotationAdd({
        type: 'text',
        x: textPosition.x,
        y: textPosition.y,
        text: textInput.trim(),
        color,
      });
    }
    setTextPosition(null);
    setTextInput('');
  };

  /**
   * Undo last annotation
   */
  const handleUndo = () => {
    const annotations = screenshot.annotations || [];
    if (annotations.length > 0) {
      onAnnotationRemove(annotations[annotations.length - 1].id);
    }
  };

  /**
   * Save annotated screenshot
   */
  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Get full resolution canvas
    const fullCanvas = document.createElement('canvas');
    const ctx = fullCanvas.getContext('2d');
    const img = imageRef.current;

    if (!ctx || !img) return;

    fullCanvas.width = img.width;
    fullCanvas.height = img.height;

    // Draw image
    ctx.drawImage(img, 0, 0);

    // Draw annotations at full resolution
    (screenshot.annotations || []).forEach((annotation) => {
      drawAnnotation(ctx, annotation, 1);
    });

    // Export as data URL
    const flattenedDataUrl = fullCanvas.toDataURL('image/png');
    onSave(flattenedDataUrl);
  };

  return (
    <div className={clsx('psc-annotator', className)}>
      {/* Toolbar */}
      <div className="psc-annotator__toolbar">
        <div className="psc-annotator__tools">
          <button
            className={clsx('psc-annotator__tool', tool === 'rectangle' && 'psc-annotator__tool--active')}
            onClick={() => setTool('rectangle')}
            title="Highlight rectangle"
          >
            <RectangleIcon />
          </button>
          <button
            className={clsx('psc-annotator__tool', tool === 'arrow' && 'psc-annotator__tool--active')}
            onClick={() => setTool('arrow')}
            title="Arrow pointer"
          >
            <ArrowIcon />
          </button>
          <button
            className={clsx('psc-annotator__tool', tool === 'text' && 'psc-annotator__tool--active')}
            onClick={() => setTool('text')}
            title="Add text"
          >
            <TextIcon />
          </button>
          <button
            className={clsx('psc-annotator__tool', tool === 'blur' && 'psc-annotator__tool--active')}
            onClick={() => setTool('blur')}
            title="Blur/redact area"
          >
            <BlurIcon />
          </button>
        </div>

        <div className="psc-annotator__colors">
          {COLORS.map((c) => (
            <button
              key={c}
              className={clsx('psc-annotator__color', color === c && 'psc-annotator__color--active')}
              style={{ backgroundColor: c }}
              onClick={() => setColor(c)}
              title={`Color: ${c}`}
            />
          ))}
        </div>

        <div className="psc-annotator__actions">
          <button
            className="psc-annotator__action"
            onClick={handleUndo}
            disabled={!screenshot.annotations?.length}
            title="Undo"
          >
            <UndoIcon />
          </button>
        </div>
      </div>

      {/* Canvas container */}
      <div className="psc-annotator__canvas-container" ref={containerRef}>
        <canvas
          ref={canvasRef}
          className="psc-annotator__canvas"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={() => {
            if (isDrawing) {
              setIsDrawing(false);
              setStartPos(null);
              renderCanvas();
            }
          }}
        />

        {/* Text input overlay */}
        {textPosition && (
          <div
            className="psc-annotator__text-input-overlay"
            style={{
              left: textPosition.x * scale,
              top: textPosition.y * scale,
            }}
          >
            <input
              type="text"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTextSubmit();
                if (e.key === 'Escape') {
                  setTextPosition(null);
                  setTextInput('');
                }
              }}
              placeholder="Enter text..."
              autoFocus
              className="psc-annotator__text-input"
              style={{ color }}
            />
            <button className="psc-annotator__text-submit" onClick={handleTextSubmit}>
              <CheckIcon />
            </button>
            <button
              className="psc-annotator__text-cancel"
              onClick={() => {
                setTextPosition(null);
                setTextInput('');
              }}
            >
              <CloseIcon />
            </button>
          </div>
        )}
      </div>

      {/* Bottom actions */}
      <div className="psc-annotator__bottom-actions">
        <button className="psc-button psc-button--secondary" onClick={onCancel}>
          Cancel
        </button>
        <button className="psc-button psc-button--primary" onClick={handleSave}>
          Save Annotations
        </button>
      </div>
    </div>
  );
}
